<template>
  <v-card>
    <v-toolbar flat dense color="primary" dark>
      <v-toolbar-title>
        <v-icon left>mdi-tag-multiple-outline</v-icon>
        {{ $t("Tarif préférentiel — fidélité") }}
      </v-toolbar-title>
      <v-spacer />
      <v-btn icon @click="$emit('close')">
        <v-icon>mdi-close</v-icon>
      </v-btn>
    </v-toolbar>

    <v-card-text class="pt-4">
      <div class="text-body-2 grey--text mb-3">
        <strong>{{ data.user_label || $t("Utilisateur") }}</strong>
      </div>

      <v-alert v-if="!loading && (!data.items || !data.items.length)" type="info" text>
        {{ $t("Cet utilisateur n'a aucun compte de fidélité actif. Le tarif préférentiel pourra être défini une fois qu'il aura accumulé des points dans une société.") }}
      </v-alert>

      <v-progress-linear v-if="loading" indeterminate color="primary" />

      <v-simple-table v-if="!loading && data.items && data.items.length" class="rate-table">
        <template v-slot:default>
          <thead>
            <tr>
              <th>{{ $t("Société") }}</th>
              <th class="text-end">{{ $t("Points") }}</th>
              <th class="text-end">{{ $t("Taux par défaut") }}</th>
              <th>{{ $t("Tarif préférentiel") }}</th>
              <th class="text-center">{{ $t("Action") }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="item in data.items" :key="item.company_id">
              <td>{{ item.company_name }}</td>
              <td class="text-end">{{ formatPoints(item.total_points) }}</td>
              <td class="text-end">
                <span v-if="item.default_point_value_currency != null">
                  {{ formatRate(item.default_point_value_currency) }} XOF/pt
                </span>
                <span v-else class="grey--text">—</span>
              </td>
              <td>
                <v-text-field
                  v-model="edits[item.company_id]"
                  :placeholder="placeholderFor(item)"
                  type="number"
                  step="0.0001"
                  min="0"
                  outlined
                  dense
                  hide-details
                  clearable
                  prepend-inner-icon="mdi-currency-eur"
                  style="max-width: 200px;"
                />
              </td>
              <td class="text-center">
                <v-btn
                  icon
                  small
                  color="primary"
                  :loading="savingMap[item.company_id]"
                  :title="$t('Sauvegarder')"
                  @click="save(item)"
                >
                  <v-icon small>mdi-content-save</v-icon>
                </v-btn>
                <v-btn
                  v-if="item.custom_point_value_currency != null"
                  icon
                  small
                  color="error"
                  :title="$t('Effacer le tarif préférentiel')"
                  :loading="savingMap[item.company_id]"
                  @click="clear(item)"
                >
                  <v-icon small>mdi-eraser</v-icon>
                </v-btn>
              </td>
            </tr>
          </tbody>
        </template>
      </v-simple-table>

      <div class="text-caption grey--text mt-3">
        <v-icon x-small color="info">mdi-information-outline</v-icon>
        {{ $t("Le tarif préférentiel remplace le taux par défaut de la société pour cet utilisateur uniquement.") }}
        {{ $t("Laissez vide et cliquez sur la gomme pour revenir au taux par défaut.") }}
      </div>
    </v-card-text>

    <v-card-actions class="pa-4">
      <v-spacer />
      <v-btn text @click="$emit('close')">{{ $t("Fermer") }}</v-btn>
    </v-card-actions>
  </v-card>
</template>

<script>
import AdminServices from "@/services/AdminServices"

export default {
  name: "AdminUserCustomRateDialog",
  props: {
    userId: { type: [Number, String], required: true },
  },

  data() {
    return {
      loading: false,
      data: { user_label: "", items: [] },
      edits: {},
      savingMap: {},
    }
  },

  watch: {
    userId: {
      immediate: true,
      handler(v) {
        if (v) this.fetch()
      },
    },
  },

  methods: {
    formatPoints(v) {
      return Number(v || 0).toLocaleString("fr-FR")
    },
    formatRate(v) {
      const n = Number(v || 0)
      return n.toLocaleString("fr-FR", { maximumFractionDigits: 4 })
    },
    placeholderFor(item) {
      if (item.default_point_value_currency != null) {
        return `${this.$t("Défaut")} : ${this.formatRate(item.default_point_value_currency)}`
      }
      return this.$t("Saisir un taux")
    },

    fetch() {
      this.loading = true
      AdminServices.getUserCustomRates(this, this.userId)
        .then(r => {
          this.data = r || { items: [] }
          // Pre-remplir les champs avec les taux preferentiels actuels
          const e = {}
          for (const it of (this.data.items || [])) {
            e[it.company_id] = it.custom_point_value_currency != null
              ? Number(it.custom_point_value_currency)
              : ""
          }
          this.edits = e
        })
        .catch(() => {
          this.$store.commit("app/message", {
            msg: this.$t("Impossible de charger les tarifs"), color: "error",
          })
        })
        .finally(() => { this.loading = false })
    },

    save(item) {
      const raw = this.edits[item.company_id]
      const value = (raw === "" || raw == null) ? null : Number(raw)
      if (value != null && (isNaN(value) || value < 0)) {
        this.$store.commit("app/message", {
          msg: this.$t("Valeur invalide"), color: "error",
        })
        return
      }
      this.$set(this.savingMap, item.company_id, true)
      AdminServices.updateUserCustomRate(this, this.userId, {
        company_id: item.company_id,
        custom_point_value_currency: value,
      })
        .then(r => {
          item.custom_point_value_currency = r.custom_point_value_currency
          item.effective_point_value = r.effective_point_value
          this.$store.commit("app/message", {
            msg: this.$t("Tarif mis à jour"), color: "success",
          })
        })
        .catch(err => {
          const msg = err?.response?.data?.detail
            || err?.response?.data?.custom_point_value_currency
            || this.$t("Échec de la mise à jour")
          this.$store.commit("app/message", { msg, color: "error" })
        })
        .finally(() => { this.$set(this.savingMap, item.company_id, false) })
    },

    clear(item) {
      this.$set(this.edits, item.company_id, "")
      this.save(item)
    },
  },
}
</script>

<style scoped>
.rate-table th {
  background: #fafafa;
  font-size: 12px;
  text-transform: uppercase;
}
</style>
